/* import __COLOCATED_TEMPLATE__ from './phone-details.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type { CallRecording } from 'embercom/objects/inbox/call-recording';
import type SidebarCallDetails from 'embercom/objects/inbox/sidebar-call-details';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import type Session from 'embercom/services/session';

interface Args {
  callId: number;
  callDetails: SidebarCallDetails;
  adminAssignee: AdminSummary | undefined;
  userSummary: UserSummary | undefined;
  isCollapsed: boolean;
  closeSidebar: () => void;
  goBack: () => void;
  deleteRecording: () => void;
}

type Option = {
  text: string;
  icon: string;
  onSelectItem: () => void;
  isDestructive: boolean;
  isDisabled: boolean;
};

export default class Inbox2PhoneDetails extends Component<Args> {
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @service declare session: Session;

  @tracked selectedTab = 'phone';
  @tracked showConfirmationModal = false;
  @tracked openSectionId = '';
  @tracked recordingToDelete: CallRecording | null = null;

  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;

  @action
  changeTab(tab: string) {
    this.selectedTab = tab;

    if (tab === 'main' && this.args.callDetails.transcript) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'viewed',
        object: 'transcript_tab',
        place: 'phone_sidebar',
      });
    }

    if (tab === 'fin' && this.args.callDetails.finLegJsonTranscript) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'viewed',
        object: 'fin_transcript_tab',
        place: 'phone_sidebar',
      });
    }
  }

  @action
  handleRecordingPlayback(legType: 'main' | 'fin' = 'main') {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'playback_triggered',
      object: 'recording',
      place: 'phone_sidebar',
      leg_type: legType,
    });
  }

  get admin() {
    return this.inbox2AssigneeSearch.findAdminById(Number(this.args.callDetails.adminId));
  }

  private downloadFile(url: string): void {
    safeWindowOpen(url, '_blank');
  }

  @action
  enableConfirmationModal(recording: CallRecording) {
    this.recordingToDelete = recording;
    this.showConfirmationModal = true;
  }

  @action
  performDelete() {
    if (this.recordingToDelete) {
      this.args.deleteRecording();
      this.recordingToDelete = null;
    }
    this.showConfirmationModal = false;
  }

  get transcriptStatusText() {
    if (!this.args.callDetails?.additionalCallDetailsLoaded) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.loading');
    }

    if (
      this.args.callDetails.transcriptStatus === 'not_submitted' ||
      this.args.callDetails.transcriptStatus === 'submitted'
    ) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.processing');
    }

    if (this.args.callDetails.recordingDeleted) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.deleted');
    }

    return this.intl.t('inbox.phone-details-sidebar.transcript.none');
  }

  get finTranscriptStatusText() {
    if (!this.args.callDetails?.additionalCallDetailsLoaded) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.loading');
    }

    if (!this.args.callDetails.finLegJsonTranscript) {
      return this.intl.t('inbox.phone-details-sidebar.transcript.none');
    }

    return null;
  }

  @action
  getRecordingDropdownOptions(recording: CallRecording): Array<{ items: Option[] }> {
    if (!recording) {
      return [{ items: [] }];
    }

    let recordingUrl = recording.recordingUrl;
    let transcriptUrl = recording.transcriptUrl;
    let hasTranscript = !!recording.jsonTranscript?.length;

    let optionList: Option[];
    let downloadRecording: Option;
    let downloadTranscript: Option;
    let deleteAll: Option;

    downloadRecording = {
      text: this.intl.t('inbox.phone-details-sidebar.download.recording'),
      icon: 'download',
      onSelectItem: () => (recordingUrl ? this.downloadFile(recordingUrl) : undefined),
      isDestructive: false,
      isDisabled: !recordingUrl || recording.isDeleted,
    };

    downloadTranscript = {
      text: this.intl.t('inbox.phone-details-sidebar.download.transcript'),
      icon: 'download',
      onSelectItem: () => (transcriptUrl ? this.downloadFile(transcriptUrl) : undefined),
      isDestructive: false,
      isDisabled: !transcriptUrl || recording.isDeleted,
    };

    deleteAll = {
      text: this.intl.t('inbox.phone-details-sidebar.delete-all'),
      icon: 'trash',
      onSelectItem: () => {
        this.enableConfirmationModal(recording);
      },
      isDestructive: true,
      isDisabled:
        !(recordingUrl || hasTranscript) ||
        recording.isDeleted ||
        !this.session.teammate.permissions.canDeleteCallRecordings,
    };

    optionList = [downloadRecording];

    // Add transcript download for both legs if transcript URL exists
    if (transcriptUrl) {
      optionList.push(downloadTranscript);
    }

    // Always show delete button, but it will be disabled if no permission
    optionList.push(deleteAll);

    return [{ items: optionList }];
  }

  get showCsatRating() {
    return (
      this.args.callDetails?.csatRating !== null && this.args.callDetails?.csatRating !== 'N/A'
    );
  }

  @action
  speakerLabel(message: any) {
    let speaker = message.speaker || message.speaker_label;

    switch (speaker.substring(0, 4)) {
      case 'voic':
        return this.intl.t('inbox.phone-details-sidebar.speakers.voicemail');
      case 'spk_': {
        let speaker_number = (parseInt(speaker.substring(4, speaker.length), 10) || 0) + 1;
        return `${this.intl.t('inbox.phone-details-sidebar.speakers.speaker', {
          speakerIdentifier: speaker_number,
        })}`;
      }
      default:
        return speaker;
    }
  }

  get detailedStateString(): string {
    switch (this.args.callDetails?.detailedState) {
      case 'routing':
        return this.intl.t('calling.call-summary.detailed-state.routing');
      case 'in_queue':
        return this.intl.t('calling.call-summary.detailed-state.in-queue');
      case 'in_progress':
        return this.intl.t('calling.call-summary.detailed-state.in-progress');
      case 'on_hold':
        return this.intl.t('calling.call-summary.detailed-state.on-hold');
      case 'answered':
        return this.intl.t('calling.call-summary.detailed-state.answered');
      case 'answered_by_fin':
        return this.intl.t('calling.call-summary.detailed-state.answered_by_fin');
      case 'fin_requested_callback':
        return this.intl.t('calling.call-summary.detailed-state.fin_requested_callback');
      case 'fin_resolved':
        return this.intl.t('calling.call-summary.detailed-state.fin_resolved');
      case 'ended_by_workflow':
        return this.intl.t('calling.call-summary.detailed-state.ended-by-workflow');
      case 'voicemail_left':
        return this.intl.t('calling.call-summary.detailed-state.voicemail-left');
      case 'abandoned_in_voicemail':
        return this.intl.t('calling.call-summary.detailed-state.abandoned-in-voicemail');
      case 'abandoned_on_hold':
        return this.intl.t('calling.call-summary.detailed-state.abandoned-on-hold');
      case 'abandoned_in_queue':
        return this.intl.t('calling.call-summary.detailed-state.abandoned-in-queue');
      case 'abandoned_routing':
        return this.intl.t('calling.call-summary.detailed-state.abandoned-routing');
      case 'initiated':
        return this.intl.t('calling.call-summary.detailed-state.initiated');
      case 'no_answer':
        return this.intl.t('calling.call-summary.detailed-state.no-answer');
      case 'awaiting_callback':
        return this.intl.t('calling.call-summary.detailed-state.awaiting-callback');
      case 'answered_callback':
        return this.intl.t('calling.call-summary.detailed-state.answered-callback');
      case 'unanswered_callback':
        return this.intl.t('calling.call-summary.detailed-state.unanswered-callback');
      case 'closed_callback':
        return this.intl.t('calling.call-summary.detailed-state.closed-callback');
      case 'fin_escalation_voicemail':
        return this.intl.t('calling.call-summary.detailed-state.fin-escalation-voicemail');
      case 'transferred_to_workflow':
        return this.intl.t('calling.call-summary.detailed-state.transferred-to-workflow');
      default:
        return this.intl.t('calling.call-summary.detailed-state.unknown');
    }
  }

  get terminatingPartyText(): string {
    let type = this.args.callDetails?.terminatingPartyType;
    if (!type) {
      return '';
    }

    switch (type) {
      case 'customer':
        return this.intl.t('inbox.phone-details-sidebar.terminating-party.customer');
      case 'teammate':
        return this.intl.t('inbox.phone-details-sidebar.terminating-party.teammate');
      case 'external':
        return this.intl.t('inbox.phone-details-sidebar.terminating-party.external');
      case 'fin':
        return this.intl.t('inbox.phone-details-sidebar.terminating-party.fin');
      default:
        return this.intl.t('inbox.phone-details-sidebar.terminating-party.unknown');
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::PhoneDetails': typeof Inbox2PhoneDetails;
    'inbox2/phone-details': typeof Inbox2PhoneDetails;
  }
}
